import { catalogService } from '../../helpers/services/catalog.service'
import i18n from '@/i18n'
import { sharedHelper, filtersHelper } from '@/utils'
import { SESSION } from '@/constants/constants'
import { hasModule } from '@/utils/capability.helper'

export const state = {
  list: { data: [], pagination: { pageNumber: 0, pageSize: 0, lastPage: 0, tableRowsCount: 0 } },
  details: {},
  canExchangeItems: [],
  showingInSalesDocsItems: [],
  syncStatus: null,
  // Whether this backend has the catalogue-sync module at all. null = not yet
  // known; false once an endpoint of it has answered 404, after which nothing
  // asks again and the UI that belongs to it stays out of the way. Remembered
  // in the browser, so the one 404 it costs to find out is paid once, not on
  // every page load.
  syncModule: readSyncModuleLatch(),
  uoms: [],
  status: { list: true, detail: true, save: true },
}

function readSyncModuleLatch() {
  try { return localStorage.getItem(SESSION.CATALOG_SYNC_MODULE) === 'absent' ? false : null } catch (e) { return null }
}
function writeSyncModuleLatch(absent) {
  try {
    if (absent) localStorage.setItem(SESSION.CATALOG_SYNC_MODULE, 'absent')
    else localStorage.removeItem(SESSION.CATALOG_SYNC_MODULE)
  } catch (e) { /* storage unavailable — the in-memory flag still holds for this session */ }
}

export const CATALOG_MODULE = 'catalog'

export const GET_ALL_CATALOG = 'getAllCatalog'
export const GET_CATALOG = 'getCatalog'
export const GET_CATALOG_BY_ITEM_CODE = 'getCatalogByItemCode'
export const UPSERT_CATALOG_BY_ITEM_CODE = 'upsertCatalogByItemCode'
export const CREATE_CATALOG = 'createCatalog'
export const UPDATE_CATALOG = 'updateCatalog'
export const DELETE_CATALOG = 'deleteCatalog'
export const SYNC_CATALOG = 'syncCatalog'
export const GET_CAN_EXCHANGE_ITEMS = 'getCanExchangeItems'
export const SET_CAN_EXCHANGE = 'setCanExchange'
export const GET_SHOWING_IN_SALES_DOCS_ITEMS = 'getShowingInSalesDocsItems'
export const SET_IS_SHOWING_IN_SALES_DOCS = 'setIsShowingInSalesDocs'
export const SET_CATALOG_IS_ACTIVE = 'setCatalogIsActive'
export const GET_CATALOG_SYNC_STATUS = 'getCatalogSyncStatus'
export const GET_CATALOG_UOMS = 'getCatalogUoms'
export const CREATE_CATALOG_UOM = 'createCatalogUom'
export const UPDATE_CATALOG_UOM = 'updateCatalogUom'
export const DELETE_CATALOG_UOM = 'deleteCatalogUom'

const REQUESTING = 'requesting'
const SUCCESS = 'commonSuccess'
const FAILURE = 'commonFailure'
const LIST_SUCCESS = 'listSuccess'
const DETAIL_SUCCESS = 'detailSuccess'
const SET_ITEMS_SUCCESS = 'setItemsSuccess'
const SHOWING_ITEMS_SUCCESS = 'showingItemsSuccess'
const SYNC_STATUS_SUCCESS = 'syncStatusSuccess'
const SYNC_MODULE_ABSENT = 'syncModuleAbsent'
const SYNC_MODULE_NOT_ADVERTISED = 'syncModuleNotAdvertised'
const UOMS_SUCCESS = 'uomsSuccess'

function mapRow(item) {
  return {
    id: item.uuid,
    itemCode: item.item_code,
    description: item.description,
    canExchange: !!item.can_exchange,
    isShowingInSalesDocs: !!item.is_showing_in_sales_docs,
    isActive: item.is_active === undefined ? true : !!item.is_active,
    media: item.media || [],
    mediaUrls: item.media_urls || [],
    createdAt: filtersHelper.getDateFormat(item.created_at),
    updatedAt: filtersHelper.getDateFormat(item.updated_at),
  }
}

export const actions = {
  [GET_ALL_CATALOG]({ commit }, { filter }) {
    commit(REQUESTING, 'list')
    catalogService.getAllCatalog(filter).then((r) => {
      commit(LIST_SUCCESS, r); commit(SUCCESS)
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [GET_CATALOG]({ commit }, { id }) {
    commit(REQUESTING, 'detail')
    return catalogService.getCatalog(id).then((r) => {
      commit(DETAIL_SUCCESS, r); commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [GET_CATALOG_BY_ITEM_CODE]({ commit }, { itemCode }) {
    commit(REQUESTING, 'detail')
    return catalogService.getCatalogByItemCode(itemCode).then((r) => {
      commit(DETAIL_SUCCESS, r); commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [UPSERT_CATALOG_BY_ITEM_CODE]({ commit }, { itemCode, data }) {
    commit(REQUESTING, 'save')
    return catalogService.upsertCatalogByItemCode(itemCode, data).then((r) => {
      sharedHelper.alertSuccessMessage(i18n.t('label.updateX', [i18n.t('module.catalog')]))
      commit(DETAIL_SUCCESS, r); commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [CREATE_CATALOG]({ commit, dispatch }, { data, filter }) {
    commit(REQUESTING, 'save')
    return catalogService.createCatalog(data).then((r) => {
      sharedHelper.alertSuccessMessage(i18n.t('label.createX', [i18n.t('module.catalog')]))
      commit(SUCCESS); if (filter) dispatch(GET_ALL_CATALOG, { filter }); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [UPDATE_CATALOG]({ commit, dispatch }, { id, data, filter }) {
    commit(REQUESTING, 'save')
    return catalogService.updateCatalog(id, data).then((r) => {
      sharedHelper.alertSuccessMessage(i18n.t('label.updateX', [i18n.t('module.catalog')]))
      commit(SUCCESS); if (filter) dispatch(GET_ALL_CATALOG, { filter }); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [DELETE_CATALOG]({ commit, dispatch }, { id, filter }) {
    commit(REQUESTING, 'save')
    return catalogService.deleteCatalog(id).then((r) => {
      commit(SUCCESS); if (filter) dispatch(GET_ALL_CATALOG, { filter }); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [SYNC_CATALOG]({ commit }) {
    commit(REQUESTING, 'save')
    return catalogService.syncCatalog().then((r) => {
      const s = r?.data || {}
      // `skipped` is set when the accounting system could not be reached; nothing was touched.
      if (s.skipped) {
        sharedHelper.alertErrorMessageWithoutCode(i18n.t('catalogSync.syncSkipped'))
      } else {
        sharedHelper.alertSuccessMessage(i18n.t('catalogSync.syncResult', [s.items ?? 0, s.uoms ?? 0, s.deactivated ?? 0]))
      }
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  // Decorates the product pages; a failure here just leaves "last synced" blank.
  [GET_CATALOG_SYNC_STATUS]({ commit, state }) {
    // The capabilities call at login is the one round-trip that says whether
    // this backend has the module. Only a backend that says so is asked.
    if (!hasModule('catalog_sync')) { commit(SYNC_MODULE_NOT_ADVERTISED); return Promise.resolve(null) }
    if (state.syncModule === false) return Promise.resolve(null)

    return catalogService.getCatalogSyncStatus().then((r) => {
      commit(SYNC_STATUS_SUCCESS, r); return r
    }).catch((e) => {
      if (e?.response?.status === 404) commit(SYNC_MODULE_ABSENT)
      return null
    })
  },
  /**
   * Per-item UOM rows belong to the catalogue-sync module. A backend without it
   * answers 404 — which is not something to put a red toast in front of
   * somebody opening a product, and not something to ask twice. The product
   * page falls back to the read-only prices the ledger already returned.
   */
  [GET_CATALOG_UOMS]({ commit, state }, { itemCode }) {
    if (!hasModule('catalog_sync')) { commit(SYNC_MODULE_NOT_ADVERTISED); return Promise.resolve(null) }
    if (state.syncModule === false) return Promise.resolve(null)

    return catalogService.getCatalogUoms(itemCode).then((r) => {
      commit(UOMS_SUCCESS, r); return r
    }).catch((e) => {
      if (e?.response?.status === 404) { commit(SYNC_MODULE_ABSENT); commit(UOMS_SUCCESS, null); return null }
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code)
    })
  },
  [CREATE_CATALOG_UOM]({ commit }, { itemCode, data }) {
    commit(REQUESTING, 'save')
    return catalogService.createCatalogUom(itemCode, data).then((r) => {
      sharedHelper.alertSuccessMessage(i18n.t('catalogSync.unitSaved', [data?.uom || '']))
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [UPDATE_CATALOG_UOM]({ commit }, { uuid, data }) {
    commit(REQUESTING, 'save')
    return catalogService.updateCatalogUom(uuid, data).then((r) => {
      sharedHelper.alertSuccessMessage(i18n.t('catalogSync.unitSaved', [data?.uom || '']))
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [DELETE_CATALOG_UOM]({ commit }, { uuid, uom }) {
    commit(REQUESTING, 'save')
    return catalogService.deleteCatalogUom(uuid).then((r) => {
      sharedHelper.alertSuccessMessage(i18n.t('catalogSync.unitDeleted', [uom || '']))
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [GET_CAN_EXCHANGE_ITEMS]({ commit }) {
    return catalogService.getCanExchangeItems().then((r) => {
      commit(SET_ITEMS_SUCCESS, r); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code)
    })
  },
  [SET_CAN_EXCHANGE]({ commit }, { itemCode, value }) {
    commit(REQUESTING, 'save')
    return catalogService.setCanExchange(itemCode, value).then((r) => {
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [GET_SHOWING_IN_SALES_DOCS_ITEMS]({ commit }) {
    return catalogService.getShowingInSalesDocsItems().then((r) => {
      commit(SHOWING_ITEMS_SUCCESS, r); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code)
    })
  },
  [SET_IS_SHOWING_IN_SALES_DOCS]({ commit }, { itemCode, value }) {
    commit(REQUESTING, 'save')
    return catalogService.setIsShowingInSalesDocs(itemCode, value).then((r) => {
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [SET_CATALOG_IS_ACTIVE]({ commit }, { itemCode, value }) {
    commit(REQUESTING, 'save')
    return catalogService.setIsActive(itemCode, value).then((r) => {
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
}

export const mutations = {
  [LIST_SUCCESS](state, data) {
    const items = data.data || []
    state.list.pagination = sharedHelper.apiPaginationAdapter(data.pagination)
    state.list.data = data.success ? items.map(mapRow) : items
  },
  [DETAIL_SUCCESS](state, data) {
    state.details = data.data ? mapRow(data.data) : {}
  },
  [SET_ITEMS_SUCCESS](state, data) {
    state.canExchangeItems = (data.data || []).map(mapRow)
  },
  [SHOWING_ITEMS_SUCCESS](state, data) {
    state.showingInSalesDocsItems = (data.data || []).map(mapRow)
  },
  [SYNC_STATUS_SUCCESS](state, data) {
    state.syncStatus = data?.data || null
    state.syncModule = true
    writeSyncModuleLatch(false)
  },
  // Not advertised by the accounting system's capabilities: hidden for this
  // session only, not remembered — switching driver can bring it back.
  [SYNC_MODULE_NOT_ADVERTISED](state) {
    state.syncModule = false
    state.syncStatus = null
    state.uoms = []
  },
  [SYNC_MODULE_ABSENT](state) {
    state.syncModule = false
    state.syncStatus = null
    state.uoms = []
    writeSyncModuleLatch(true)
  },
  [UOMS_SUCCESS](state, data) {
    // Kept as the API shape (uom / rate / price / is_default / editable) — the
    // product detail renders these rows alongside `prices`, which share it.
    state.uoms = data?.data || []
  },
  [FAILURE](state) { state.status = { list: true, detail: true, save: true } },
  [REQUESTING](state, type) { state.status[type] = false },
  [SUCCESS](state) { state.status = { list: true, detail: true, save: true } },
}
